import { useState } from "react";
import dynamic from 'next/dynamic';
// @mui
import {
  Avatar,
  Box,
  Button,
  Card,
  Table,
  TableBody,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  Pagination,
} from "@mui/material";
// hooks
import useResponsive from "../../hooks/useResponsive";
// components
import Label from '../../components/Label';
import Iconify from "../../components/Iconify";
import SvgIconStyle from "../../components/SvgIconStyle";
import Scrollbar from "../../components/Scrollbar";
// lottie
const DotLottiePlayer = dynamic(
  () => import('@dotlottie/react-player').then(mod => mod.DotLottiePlayer),
  { ssr: false }
);
import '@dotlottie/react-player/dist/index.css';

// ----------------------------------------------------------------------

export default function ScoutPoolTable({
  summaryData,
  poolData,
  scoutData,
  currentTab,
  setCurrentTab,
  selectCrew=null,
  // pagination
  pageCount,
  page,
  setPage,
}) {
  const smUp = useResponsive('up', 'sm');

  const TABS = [
    {
      label: '인재풀 전체',
      value: 'all',
      color: 'primary',
      count: summaryData?.all || 0,
    },
    {
      label: '커피챗 제안 가능',
      value: 'suggestable',
      color: 'success',
      count: summaryData?.suggestable || 0,
    },
    {
      label: '제안 완료',
      value: 'sent',
      color: 'default',
      count: summaryData?.sent || 0,
    },
    {
      label: '일정 확정 필요',
      value: 'confirm',
      color: 'info',
      count: summaryData?.confirm || 0,
    },
    {
      label: '미팅 예정/완료',
      value: 'reserved',
      color: 'default',
      count: summaryData?.reserved || 0,
    },
    {
      value: 'archived',
      label: '인재풀 제외',
      color: 'default',
      count: 0,
    }
  ];
  const isArchived = currentTab === "archived";
  const noScoutData = scoutData?.length === 0;
  const noCoffeeChatData = poolData?.length === 0;
  const hasPages = pageCount > 1;

  return (
    <Card>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pr: 3 }}
      >
        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ px: 3 }}
          TabIndicatorProps={{
            style: { ...currentTab === "archived" && { display: 'none' } }
          }}
        >
          {TABS.map((tab, index) => (
            <Tab
              sx={{ ...tab.value === "archived" && { visibility: "hidden" } }}
              disabled={tab.count === 0}
              disableRipple
              key={index}
              value={tab.value}
              label={
                <Stack spacing={1} direction="row" alignItems="center">
                  <div>{tab.label}</div><Label variant={currentTab === tab.value ? "filled" : "ghost"} color={tab.color}>{tab.count}</Label>
                </Stack>
              }
            />
          ))}
        </Tabs>
        <IconButton size="small" sx={{ color: "text.disabled" }} edge="end" onClick={() => {setCurrentTab("archived")}}>
          <SvgIconStyle src={"/icons/icon-archive-drop-mono.svg"} sx={{ width: 24, height: 24 }} />
        </IconButton>
      </Stack>
      <Scrollbar>
        <Table size="medium">
          {/* head */}
          <TableHead sx={{ whiteSpace: "nowrap" }}>
            <TableRow>
              <TableCell
                align="left"
                sx={{ 
                  borderRadius: "0!important", boxShadow: "none!important",
                  color: noCoffeeChatData ? "text.disabled" : "text.primary",
                  bgcolor: "grey.100",
                  minWidth: "220px",
                }}
              >
                크루 닉네임
              </TableCell>
              <TableCell
                align="left"
                sx={{ 
                  color: noCoffeeChatData ? "text.disabled" : "text.primary",
                  bgcolor: "grey.100",
                }}
              >
                포지션
              </TableCell>
              <TableCell
                align="left"
                sx={{ 
                  color: noCoffeeChatData ? "text.disabled" : "text.primary",
                  bgcolor: "grey.100",
                }}
              >
                커피챗 상태
              </TableCell>
              <TableCell
                align="left"
                sx={{ 
                  borderRadius: "0!important", boxShadow: "none!important",
                  color: noCoffeeChatData ? "text.disabled" : "text.primary",
                  bgcolor: "grey.100",
                }}
              >
                추천 사유
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {poolData && poolData.map((row, index) => (
              <TableRow hover
                key={index}
                sx={{
                  "&:hover": {
                    backgroundColor: "grey.100",
                    "& .detailbtn": {
                      opacity: 1,
                    }
                  },
                }}
              >
                <TableCell align="left" sx={{ maxWidth: 300 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ position: "relative" }}>
                    <Avatar
                      src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${row?.crew?.randomhash || '5'}.png`}
                      alt={row?.crew?.nickname}
                      color={'default'}
                      sx={{
                        width: 40,
                        height: 40,
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {row?.crew?.nickname}
                    </Typography>

                    {/* drawer btn */}
                    <Stack
                      className='detailbtn'
                      direction="row"
                      justifyContent={"flex-end"}
                      sx={{
                        width: "calc(100% - 32px)",
                        position: "absolute",
                        right: 2,
                        opacity: 0,
                      }}
                    >
                      <Button
                        sx={{
                          backgroundColor: "white",
                          "&:hover": {
                            backgroundColor: "grey.300"
                          }
                        }}
                        variant="outlined"
                        size="small"
                        color="inherit"
                        endIcon={<Iconify icon="ic:chevron-right" sx={{ ml: -0.5 }} />}
                        onClick={() => selectCrew && selectCrew(row)}
                      >
                        자세히보기
                      </Button>
                    </Stack>
                  </Stack>
                </TableCell>

                <TableCell align="left" sx={{ maxWidth: 250 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis" }} noWrap>
                    {row?.crew?.main_position}
                  </Typography>                  
                </TableCell>

                <TableCell align="left">
                  {isArchived ? (
                    <Label variant="outlined" color="default" sx={{ color: "text.secondary" }}>
                      인재풀 제외
                    </Label>
                  ) : (
                    <Label
                      color={
                          row?.coffee_chat_status === "wait" ? "success"
                        : row?.coffee_chat_status === "offer-to-company" ? "info"
                        : "default"
                      }
                    >
                      {row?.label}
                    </Label>
                  )}
                </TableCell>

                <TableCell align="left" sx={{ minWidth: 200 }}>
                {!isArchived && row?.scouted_crew?.reason?.length > 0 && row?.scouted_crew?.reason?.split('\n').filter(m => (m && m !== "\r")).map((reason, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                    <SvgIconStyle src={"/icons/icon-check-mono.svg"} sx={{ minWidth: 16, minHeight: 16, maxWidth: 16, maxHeight: 16, my: 0.125 }} />
                    <Typography variant="caption">
                      {reason}
                    </Typography>
                  </Stack>
                ))}
                {isArchived && row?.reason?.length > 0 && row?.reason?.split('\n').filter(m => (m && m !== "\r")).map((reason, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                    <SvgIconStyle src={"/icons/icon-check-mono.svg"} sx={{ minWidth: 16, minHeight: 16, maxWidth: 16, maxHeight: 16, my: 0.125 }} />
                    <Typography variant="caption">
                      {reason}
                    </Typography>
                  </Stack>
                ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>

      {hasPages && (
        <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            size={"medium"}
            count={pageCount}
            page={page}
            onChange={(e, v) => setPage(v)}
          />
        </Box>
      )}

      {isArchived && noCoffeeChatData && (
        <Box sx={{ px: 3, py: 7, mx: "auto" }} maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: 1,
              alignItems: "center", justifyContent: "center", textAlign: "center",
            }}
          >
            <Iconify icon="eva:alert-triangle-fill" width={24} height={24} sx={{ color: "text.disabled", mb: 1 }} />
            <Typography variant="subtitle1" gutterBottom>
              인재풀에서 제외한 크루가 없어요.
            </Typography>
            <Typography variant="caption">
              추천 받은 크루를 확인하고, 마음에 드는 크루를 인재풀에 추가해주세요.
            </Typography>
          </Box>
        </Box>
      )}

      {/* no scout & coffeechat data */}
      {!isArchived && !noScoutData && noCoffeeChatData && (
        <Box sx={{ px: 3, py: 7, mx: "auto" }} maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: 1,
              alignItems: "center", justifyContent: "center", textAlign: "center",
            }}
          >
            <Iconify icon="eva:alert-triangle-fill" width={24} height={24} sx={{ color: "text.disabled", mb: 1 }} />
            <Typography variant="subtitle1" gutterBottom>
              인재풀에 크루가 없어요.
            </Typography>
            <Typography variant="caption">
              추천 받은 크루를 확인하고, 마음에 드는 크루를 인재풀에 추가해주세요.
            </Typography>
          </Box>
        </Box>
      )}

      {/* no coffeechat */}
      {!isArchived && noScoutData && noCoffeeChatData && (
        <Box sx={{ px: 3, py: 7, mx: "auto" }} maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: 1,
              alignItems: "center", justifyContent: "center", textAlign: "center",
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              요청하신 인재를 찾고 있어요.
            </Typography>
            <Typography variant="caption">
              {smUp && '적합한 '}인재를 찾으면 카카오톡으로 알려드릴게요.
            </Typography>
            <DotLottiePlayer
              autoplay
              loop
              mode="normal"
              src={'/lottie/scout-finding.lottie'}
              style={{ width: smUp ? 350 : 250, height: smUp ? 200 : 150, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
            />
          </Box>
        </Box>
      )}
    </Card>
  )
}